import { useEffect, useRef } from 'react';
import { Descendant, Editor, Transforms } from 'slate';
import { isEqual } from 'lodash';

/*
1.Synchronizes Slate editor content with external data sources
2.Prevents infinite re-render loops through debouncing and processing flags
3.Handles editor state updates safely with proper error handling
4.Optimizes performance by avoiding unnecessary updates through deep equality checks */

export const useEditorSync = (editor: Editor, editorInitialData: Descendant[]) => {
    const prevEditorDataRef = useRef<Descendant[]>(editorInitialData);
    const debouncedSyncRef = useRef<NodeJS.Timeout>();
    const isProcessingRef = useRef(false);

    useEffect(() => {
        if (!editorInitialData || editorInitialData.length === 0 || isProcessingRef.current) {
            return;
        }
        if (debouncedSyncRef.current) {
            clearTimeout(debouncedSyncRef.current);
        }
        if (editorInitialData === prevEditorDataRef.current) {
            return;
        }
        debouncedSyncRef.current = setTimeout(() => {
            if (isProcessingRef.current) return;

            try {
                isProcessingRef.current = true;
                const isDataEqual = isEqual(editor.children, editorInitialData);
                if (!isDataEqual) {
                    Editor.withoutNormalizing(editor, () => {
                        try {
                            const editorRange = Editor.range(editor, []);
                            Transforms.select(editor, editorRange);
                            if (editor.children.length > 0) {
                                Transforms.delete(editor, { at: editorRange });
                            }
                            if (editorInitialData && editorInitialData.length > 0) {
                                Transforms.insertNodes(editor, editorInitialData, { at: [0] });
                            } else {
                                Transforms.insertNodes(editor, [{ type: 'paragraph', children: [{ text: '' }] } as unknown as Descendant], {
                                    at: [0],
                                });
                            }

                            Transforms.select(editor, Editor.start(editor, []));
                        } catch (error) {
                            console.error('Error syncing editor state:', error);
                            editor.children =
                                editorInitialData.length > 0
                                    ? editorInitialData
                                    : [{ type: 'paragraph', children: [{ text: '' }] } as unknown as Descendant];
                            editor.onChange();
                        }
                    });
                    prevEditorDataRef.current = editorInitialData;
                }
            } finally {
                isProcessingRef.current = false;
            }
        }, 100);
    }, [editorInitialData, editor]);

    useEffect(() => {
        return () => {
            if (debouncedSyncRef.current) {
                clearTimeout(debouncedSyncRef.current);
            }
        };
    }, []);
};
